import { differenceBy, sortBy } from 'lodash-es';

import { CaregiverQualification } from '@pflegia/types/api/candidate/care-giver-qualification';
import { MAX_JOB_TITLE_ANSWERS } from '@pflegia/types/api/candidate/job-title';
import { FunnelQuestion } from '@pflegia/types/api/funnel/questions';
import { ConfirmationStatus } from '@pflegia/types/api/general';
import { JobTitleCategory, RequiredJobTitle } from '@pflegia/types/api/job-title';

import { parseCandidateAnswers } from '../parse-candidate-answers';
import { getMainPriorityJobTitle } from './get-main-priority-job-title';

type JobTitleWithQualification = {
  jobTitle: RequiredJobTitle;
  hasQualification: ConfirmationStatus | CaregiverQualification | null;
};

/**
 * Gets job titles for the candidate's job-title answers entity.
 * The job titles are sorted by the main priority job title
 * followed by similar job titles (i.e. job titles with the same tracking priority and category),
 * then followed by the other job titles (i.e. sorted by tracking priority).
 */
export const getJobTitleForAnswers = (
  jobTitles: RequiredJobTitle[],
  answers: string,
): { jobTitle: RequiredJobTitle; hasQualification: ConfirmationStatus | CaregiverQualification | null }[] => {
  const mainPrioritySpec = getMainPriorityJobTitle(jobTitles);
  if (!mainPrioritySpec) return [];

  const parsedAnswers = parseCandidateAnswers(answers);
  const hasQualification = parsedAnswers?.[FunnelQuestion.JobQualification] ?? null;
  const { similarJobTitles, frequency, jobTitle: mainJobTitle, category } = mainPrioritySpec;
  const qualifiedJobTitles = [mainJobTitle].concat(similarJobTitles);
  const jobTitleAnswers = qualifiedJobTitles.map(jobTitle => ({
    jobTitle,
    hasQualification,
  })) as JobTitleWithQualification[];

  if (frequency >= MAX_JOB_TITLE_ANSWERS) {
    return jobTitleAnswers.slice(0, MAX_JOB_TITLE_ANSWERS);
  }

  const sortedAdditional = sortBy(differenceBy(jobTitles, qualifiedJobTitles, 'title'), ['trackingPriority']);
  const otherJobTitles = sortedAdditional.slice(0, MAX_JOB_TITLE_ANSWERS - jobTitleAnswers.length);
  const otherAnswers = otherJobTitles.map(jobTitle => {
    const otherQualification = jobTitle.trackingPriority[0] === mainJobTitle.trackingPriority[0] ? hasQualification : null;
    // Caregiver Qualifications are not shared with any other Job Title Qualification.
    return { jobTitle, hasQualification: category === JobTitleCategory.Companion ? null : otherQualification };
  });
  return jobTitleAnswers.concat(otherAnswers);
};
